import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  AdicionarApoioEntrada,
  ChamadoDetalheDto,
  ConexaoDto,
  ConversaDto,
  CriarConexaoEntrada,
  ImportacaoDto,
  MensagemDto,
  OcorrenciaDto,
  QrCodeDto,
  TransferirChamadoEntrada,
  ContagemPorEscopo,
  PedidoDeApoioDto,
} from '@crm/shared'
import { api, enviarArquivo } from './cliente'

export interface FiltroConversas {
  conexaoId: string
  status: 'aberto' | 'fechado' | 'todas'
  escopo: 'todas' | 'minhas' | 'fila'
  busca?: string
  /** Palavra dita em alguma mensagem da conversa. */
  mensagem?: string
  protocolo?: string
  responsavelId?: string
  tagId?: string
  funilId?: string
  etapaId?: string
  /** Abertura em "abertos", encerramento em "encerrados". */
  de?: string
  ate?: string
}

export const chavesChat = {
  conexoes: ['conexoes'] as const,
  qrcode: (id: string) => ['qrcode', id] as const,
  importacao: (id: string) => ['importacao', id] as const,
  conversas: (filtro: FiltroConversas) => ['conversas', filtro] as const,
  chamado: (id: string) => ['chamado', id] as const,
  // Dentro de "conversas": o que recarrega a lista recarrega também a conversa aberta
  // por fora dela (um link, um aviso), e os quadradinhos do funil não ficam velhos.
  conversaAvulsa: (id: string) => ['conversas', 'avulsa', id] as const,
  mensagens: (id: string) => ['mensagens', id] as const,
}

// ─────────────────────────── conexões ───────────────────────────

export function useConexoes(opcoes: { intervalo?: number } = {}) {
  return useQuery({
    queryKey: chavesChat.conexoes,
    queryFn: () => api<ConexaoDto[]>('/api/conexoes'),
    // A faixa de aviso do topo pede uma conferência de tempos em tempos: a
    // queda de um número não vem de uma ação de quem está na tela.
    refetchInterval: opcoes.intervalo,
  })
}

export function useQrCode(conexaoId: string | null, ativo: boolean) {
  return useQuery({
    queryKey: chavesChat.qrcode(conexaoId ?? ''),
    queryFn: () => api<QrCodeDto>(`/api/conexoes/${conexaoId}/qrcode`),
    enabled: Boolean(conexaoId) && ativo,
    // O QR da Evolution expira em segundos; sem isso a tela mostra um código morto.
    refetchInterval: 25_000,
    staleTime: 0,
  })
}

/**
 * Importar o histórico de um número já conectado.
 *
 * O que veio antes da conexão o WhatsApp entrega uma vez só, no pareamento, e
 * não pelo webhook — por isso é um botão, e não algo automático.
 */
export function useImportarHistorico() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (conexaoId: string) =>
      api<ImportacaoDto>(`/api/conexoes/${conexaoId}/importar-historico`, { metodo: 'POST' }),
    onSuccess: (_, conexaoId) =>
      cliente.invalidateQueries({ queryKey: chavesChat.importacao(conexaoId) }),
  })
}

/** Andamento. Consulta de perto enquanto roda, e para quando termina. */
export function useImportacao(conexaoId: string | null, ativo: boolean) {
  return useQuery({
    queryKey: chavesChat.importacao(conexaoId ?? ''),
    queryFn: () => api<ImportacaoDto>(`/api/conexoes/${conexaoId}/importacao`),
    enabled: Boolean(conexaoId) && ativo,
    // Enquanto a janela estiver aberta, consulta sempre. Condicionar o
    // intervalo ao estado anterior deixava o acompanhamento parado em "0 de 0":
    // a primeira resposta é "nunca", que desliga o relógio antes de a
    // importação sequer começar.
    refetchInterval: 1500,
    staleTime: 0,
  })
}

export function useCriarConexao() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (dados: CriarConexaoEntrada) =>
      api<ConexaoDto>('/api/conexoes', { metodo: 'POST', corpo: dados }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesChat.conexoes }),
  })
}

export function useAtualizarConexao() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Record<string, unknown> }) =>
      api<ConexaoDto>(`/api/conexoes/${id}`, { metodo: 'PATCH', corpo: dados }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesChat.conexoes }),
  })
}

export function useDefinirAcessoConexao() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, usuarioIds }: { id: string; usuarioIds: string[] }) =>
      api<ConexaoDto>(`/api/conexoes/${id}/acesso`, { metodo: 'PUT', corpo: { usuarioIds } }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesChat.conexoes }),
  })
}

export function useDesconectarConexao() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean }>(`/api/conexoes/${id}/desconectar`, { metodo: 'POST' }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesChat.conexoes }),
  })
}

export function useExcluirConexao() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<{ ok: boolean }>(`/api/conexoes/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: chavesChat.conexoes }),
  })
}

// ─────────────────────────── conversas ───────────────────────────

/**
 * Quantas conversas há em cada aba.
 *
 * Usa a mesma chave de filtro da lista **sem o escopo**: as três contagens
 * saem juntas, então trocar de aba não deve disparar consulta nova — o número
 * já está na tela antes do clique.
 */
export function useContagens(filtro: FiltroConversas, habilitado: boolean) {
  const { escopo: _escopo, ...semEscopo } = filtro
  return useQuery({
    /*
      A chave começa com `conversas` de propósito.

      Tudo que mexe na lista mexe nas contagens: mensagem nova pelo socket,
      marcar como lida, encerrar, transferir, pegar da fila. Cada um desses
      caminhos já invalida `['conversas']`, e com o prefixo compartilhado o
      contador vem junto de graça — em vez de eu ter de lembrar de invalidar
      duas chaves em cada um deles, e esquecer numa.
    */
    queryKey: ['conversas', 'contagens', semEscopo],
    queryFn: () =>
      api<ContagemPorEscopo>('/api/chamados/contagens', {
        query: {
          conexaoId: filtro.conexaoId,
          status: filtro.status,
          // O escopo vai fixo: a rota devolve os três de qualquer jeito, e
          // mandá-lo variável faria a chave mudar a cada troca de aba.
          escopo: 'todas',
          busca: filtro.busca,
          mensagem: filtro.mensagem,
          protocolo: filtro.protocolo,
          responsavelId: filtro.responsavelId,
          tagId: filtro.tagId,
          funilId: filtro.funilId,
          etapaId: filtro.etapaId,
          de: filtro.de,
          ate: filtro.ate,
        },
      }),
    enabled: habilitado,
  })
}

export function useConversas(filtro: FiltroConversas, habilitado: boolean) {
  return useQuery({
    queryKey: chavesChat.conversas(filtro),
    queryFn: () =>
      api<ConversaDto[]>('/api/chamados/conversas', {
        query: {
          conexaoId: filtro.conexaoId,
          status: filtro.status,
          escopo: filtro.escopo,
          busca: filtro.busca,
          mensagem: filtro.mensagem,
          protocolo: filtro.protocolo,
          responsavelId: filtro.responsavelId,
          tagId: filtro.tagId,
          funilId: filtro.funilId,
          etapaId: filtro.etapaId,
          de: filtro.de,
          ate: filtro.ate,
        },
      }),
    enabled: habilitado && Boolean(filtro.conexaoId),
    // O cronômetro anda mesmo sem evento novo; sem isso ele congela na tela.
    refetchInterval: 30_000,
  })
}

export function useChamado(id: string | null) {
  return useQuery({
    queryKey: chavesChat.chamado(id ?? ''),
    queryFn: () => api<ChamadoDetalheDto>(`/api/chamados/${id}`),
    enabled: Boolean(id),
  })
}

/**
 * O cartão de uma conversa que não está na lista carregada.
 *
 * Só consulta quando a lista realmente não tem o chamado — é o caso de quem
 * chega por link com o filtro apontando para outro lugar. Na navegação normal
 * fica desligado, sem pedir nada a mais ao servidor.
 */
export function useConversaAvulsa(chamadoId: string | null, precisa: boolean) {
  return useQuery({
    queryKey: chavesChat.conversaAvulsa(chamadoId ?? ''),
    queryFn: () => api<ConversaDto>(`/api/chamados/${chamadoId}/conversa`),
    enabled: Boolean(chamadoId) && precisa,
  })
}

/**
 * As mensagens da conversa. Com `aoRedorDe`, as que cercam aquela mensagem.
 *
 * É o que a lupa da busca usa: a mensagem achada pode ser de meses atrás, e a
 * tela carrega só as últimas. A chave inclui o alvo, senão a conversa abriria
 * com a janela guardada da vez anterior.
 */
export function useMensagens(chamadoId: string | null, aoRedorDe: string | null = null) {
  return useQuery({
    queryKey: [...chavesChat.mensagens(chamadoId ?? ''), aoRedorDe],
    queryFn: () =>
      api<MensagemDto[]>(`/api/chamados/${chamadoId}/mensagens`, {
        query: aoRedorDe ? { aoRedorDe } : undefined,
      }),
    enabled: Boolean(chamadoId),
    // Pulando de uma ocorrência à outra, a conversa anterior fica na tela até
    // a nova janela chegar: sem isso a conversa pisca em branco a cada seta.
    placeholderData: keepPreviousData,
  })
}

/**
 * Todas as vezes em que a palavra aparece nesta conversa, da mais antiga à
 * mais nova. É o que as setas de navegação percorrem.
 */
export function useOcorrencias(chamadoId: string | null, termo: string | null) {
  const procurando = Boolean(chamadoId) && (termo?.trim().length ?? 0) >= 3
  return useQuery({
    queryKey: ['ocorrencias', chamadoId, termo],
    queryFn: () =>
      api<OcorrenciaDto[]>(`/api/chamados/${chamadoId}/ocorrencias`, {
        query: { termo: termo ?? '' },
      }),
    enabled: procurando,
  })
}

export function invalidarChat(cliente: ReturnType<typeof useQueryClient>, chamadoId?: string) {
  void cliente.invalidateQueries({ queryKey: ['conversas'] })
  if (chamadoId) {
    void cliente.invalidateQueries({ queryKey: chavesChat.mensagens(chamadoId) })
    void cliente.invalidateQueries({ queryKey: chavesChat.chamado(chamadoId) })
  }
}

export function useEnviarMensagem() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({
      chamadoId,
      conteudo,
      notaInterna,
    }: {
      chamadoId: string
      conteudo: string
      notaInterna: boolean
    }) =>
      api<MensagemDto>(`/api/chamados/${chamadoId}/mensagens`, {
        metodo: 'POST',
        corpo: { conteudo, notaInterna },
      }),
    onSuccess: (_, variaveis) => invalidarChat(cliente, variaveis.chamadoId),
  })
}

/** Troca o texto de uma mensagem enviada (ADR 0026). O texto vai sem a assinatura. */
export function useEditarMensagem() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({
      chamadoId,
      mensagemId,
      conteudo,
    }: {
      chamadoId: string
      mensagemId: string
      conteudo: string
    }) =>
      api<MensagemDto>(`/api/chamados/${chamadoId}/mensagens/${mensagemId}`, {
        metodo: 'PATCH',
        corpo: { conteudo },
      }),
    onSuccess: (_, variaveis) => invalidarChat(cliente, variaveis.chamadoId),
  })
}

/** Apaga para todos (ADR 0026). No CRM a mensagem fica, marcada como apagada. */
export function useApagarMensagem() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ chamadoId, mensagemId }: { chamadoId: string; mensagemId: string }) =>
      api<MensagemDto>(`/api/chamados/${chamadoId}/mensagens/${mensagemId}`, {
        metodo: 'DELETE',
      }),
    onSuccess: (_, variaveis) => invalidarChat(cliente, variaveis.chamadoId),
  })
}

export function useEnviarMidia() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ chamadoId, arquivo }: { chamadoId: string; arquivo: File }) =>
      enviarArquivo<MensagemDto>(`/api/chamados/${chamadoId}/midia`, arquivo),
    onSuccess: (_, variaveis) => invalidarChat(cliente, variaveis.chamadoId),
  })
}

export function useEncerrarChamado() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({
      chamadoId,
      manterNaCarteira,
    }: {
      chamadoId: string
      manterNaCarteira: boolean
    }) =>
      api<{ ok: boolean }>(`/api/chamados/${chamadoId}/encerrar`, {
        metodo: 'POST',
        corpo: { manterNaCarteira },
      }),
    onSuccess: (_, v) => invalidarChat(cliente, v.chamadoId),
  })
}

export function useReabrirChamado() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (chamadoId: string) =>
      api<{ ok: boolean }>(`/api/chamados/${chamadoId}/reabrir`, { metodo: 'POST' }),
    onSuccess: (_, chamadoId) => invalidarChat(cliente, chamadoId),
  })
}

export function useTransferirChamado() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ chamadoId, dados }: { chamadoId: string; dados: TransferirChamadoEntrada }) =>
      api<{ ok: boolean }>(`/api/chamados/${chamadoId}/transferir`, {
        metodo: 'POST',
        corpo: dados,
      }),
    onSuccess: (_, v) => invalidarChat(cliente, v.chamadoId),
  })
}

export function useAdicionarApoio() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ chamadoId, dados }: { chamadoId: string; dados: AdicionarApoioEntrada }) =>
      api<{ ok: boolean }>(`/api/chamados/${chamadoId}/apoios`, { metodo: 'POST', corpo: dados }),
    onSuccess: (_, v) => invalidarChat(cliente, v.chamadoId),
  })
}

export function useRemoverApoio() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ chamadoId, apoioId }: { chamadoId: string; apoioId: string }) =>
      api<{ ok: boolean }>(`/api/chamados/${chamadoId}/apoios/${apoioId}`, { metodo: 'DELETE' }),
    onSuccess: (_, v) => invalidarChat(cliente, v.chamadoId),
  })
}

/** "Fechar para mim": quem está de apoio sai sozinho da conversa (pedido de 05/10/2026). */
export function useSairDoApoio() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ chamadoId, recado }: { chamadoId: string; recado?: string }) =>
      api<{ ok: boolean }>(`/api/chamados/${chamadoId}/sair-do-apoio`, {
        metodo: 'POST',
        corpo: recado ? { recado } : {},
      }),
    onSuccess: (_, v) => invalidarChat(cliente, v.chamadoId),
  })
}

// ─────────────────────────── apoio com pedido (ADR 0043) ───────────────────────────

export const CHAVE_PEDIDOS_DE_APOIO = ['apoios', 'pendentes'] as const

/** Os pedidos de apoio sem resposta de quem está olhando: alimentam o pop-up. */
export function usePedidosDeApoio(habilitado: boolean) {
  return useQuery({
    queryKey: CHAVE_PEDIDOS_DE_APOIO,
    queryFn: () => api<PedidoDeApoioDto[]>('/api/apoios/pendentes'),
    enabled: habilitado,
    staleTime: 60_000,
  })
}

export function useResponderApoio() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (v: {
      chamadoId: string
      apoioId: string
      aceitar: boolean
      comentario?: string
    }) =>
      api<{ ok: boolean }>(`/api/chamados/${v.chamadoId}/apoios/${v.apoioId}/responder`, {
        metodo: 'POST',
        corpo: { aceitar: v.aceitar, ...(v.comentario ? { comentario: v.comentario } : {}) },
      }),
    onSuccess: (_, v) => {
      void cliente.invalidateQueries({ queryKey: CHAVE_PEDIDOS_DE_APOIO })
      invalidarChat(cliente, v.chamadoId)
    },
  })
}

/** O dono abriu a conversa que voltou do apoio: sai do bloco "Saiu do apoio". */
export function useRetomei() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (chamadoId: string) =>
      api<{ ok: boolean }>(`/api/chamados/${chamadoId}/retomei`, { metodo: 'POST' }),
    onSuccess: (_, chamadoId) => invalidarChat(cliente, chamadoId),
    // Dispara sozinho ao abrir a conversa; falhar só deixa o bloco "Saiu do apoio" para depois.
    meta: { silenciosa: true },
  })
}

export function useMarcarLeitura() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ chamadoId, naoLida }: { chamadoId: string; naoLida: boolean }) =>
      api<{ ok: boolean }>(`/api/chamados/${chamadoId}/leitura`, {
        metodo: 'PATCH',
        corpo: { naoLida },
      }),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: ['conversas'] }),
  })
}

export function useAgendarRetorno() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ chamadoId, quando }: { chamadoId: string; quando: string | null }) =>
      api<{ ok: boolean }>(`/api/chamados/${chamadoId}/retorno`, {
        metodo: 'PATCH',
        corpo: { quando },
      }),
    onSuccess: (_, v) => invalidarChat(cliente, v.chamadoId),
  })
}

export function useAbrirChamado() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (dados: {
      conexaoId: string
      /** Um dos dois: contato que já existe, ou número digitado. */
      contatoId?: string
      telefone?: string
      nome?: string
    }) =>
      api<{ chamadoId: string; criado: boolean; contatoId: string; contatoCriado: boolean }>(
        '/api/chamados/abrir',
        { metodo: 'POST', corpo: dados },
      ),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: ['conversas'] }),
  })
}
